import { useMutation, useQuery } from "@tanstack/react-query";
import { CACHE_TIME } from "@/lib/cacheConfig";
import { queryKeys } from "@/lib/queryKeys";
import { useQueryInvalidation } from "@/lib/queryInvalidation";
import {
  bulkCreateManualMasterPart,
  bulkDeleteManualMasterPart,
  deleteManualMasterPart,
  getManualMasterParts,
  searchMasterPart,
  type ManualMasterPartListParams,
  type ManualMasterPartPair,
} from "@/api/manualMasterPartService";

export const useSearchMasterPart = (partNumber: string) => {
  return useQuery({
    queryKey: queryKeys.manualMasterPart.search(partNumber),
    queryFn: () => searchMasterPart(partNumber),
    staleTime: CACHE_TIME.SHORT,
    enabled: partNumber.trim().length >= 3,
  });
};

export const useGetManualMasterParts = (params: ManualMasterPartListParams) => {
  return useQuery({
    queryKey: queryKeys.manualMasterPart.list(params),
    queryFn: () => getManualMasterParts(params),
    staleTime: CACHE_TIME.SHORT,
  });
};

export const useBulkCreateManualMasterPart = () => {
  const invalidate = useQueryInvalidation();

  return useMutation({
    mutationKey: queryKeys.manualMasterPart.bulkCreate(),
    mutationFn: (pairs: ManualMasterPartPair[]) =>
      bulkCreateManualMasterPart(pairs),
    onSuccess: () => {
      invalidate.invalidateManualMasterPart();
    },
  });
};

export const useDeleteManualMasterPart = () => {
  const invalidate = useQueryInvalidation();

  return useMutation({
    mutationKey: queryKeys.manualMasterPart.delete(),
    mutationFn: (id: number) => deleteManualMasterPart(id),
    onSuccess: () => {
      invalidate.invalidateManualMasterPart();
    },
  });
};

export const useBulkDeleteManualMasterPart = () => {
  const invalidate = useQueryInvalidation();

  return useMutation({
    mutationKey: queryKeys.manualMasterPart.bulkDelete(),
    mutationFn: (ids: number[]) => bulkDeleteManualMasterPart(ids),
    onSuccess: () => {
      invalidate.invalidateManualMasterPart();
    },
  });
};
